/**
 * Step 3a: Bank Account Selector
 * 
 * User selects which bank account to receive fiat payments.
 * Shows existing linked accounts or prompts to link a new one.
 * 
 * @module addresses/wizard
 */

import React, { useState, useEffect } from 'react';
import { Heading } from "@/components/ui/typography";
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getPayoutRoutes } from '@/lib/rpc/user/payoutRoutes';
import type { PaymentRail, FiatCurrency } from './types';
import { PAYMENT_RAIL_INFO } from './types';

interface BankAccount {
  id: string;
  external_account_id: string;
  bank_name: string | null;
  account_number_masked: string;
  routing_number_masked: string;
  currency: string | null;
  account_type: string | null;
  status: string;
  country: string | null;
}

interface PayoutRoute {
  id: string;
  external_account_id: string;
  payment_rail: PaymentRail;
  currency: FiatCurrency;
  bank_account?: BankAccount;
}

interface BankAccountSelectorProps {
  selectedPayoutRouteId?: string;
  selectedPaymentRail?: PaymentRail;
  selectedCurrency?: FiatCurrency;
  onPayoutRouteSelect: (routeId: string, rail: PaymentRail, currency: FiatCurrency) => void;
  onLinkNewAccount: () => void;
}

export const BankAccountSelector: React.FC<BankAccountSelectorProps> = ({
  selectedPayoutRouteId,
  onPayoutRouteSelect,
  onLinkNewAccount,
}) => {
  const [payoutRoutes, setPayoutRoutes] = useState<PayoutRoute[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Detect payment rail from country
  const detectPaymentRail = (country: string | null, currency: string | null): PaymentRail => {
    if (country === 'US' || currency === 'USD') return 'ach';
    if (country === 'MX' || currency === 'MXN') return 'spei';
    if (['DE', 'FR', 'ES', 'IT', 'NL', 'BE', 'AT', 'PT', 'IE', 'FI'].includes(country || '') || currency === 'EUR') return 'sepa';
    return 'ach';
  };

  // Fetch user's payout routes (linked bank accounts)
  useEffect(() => {
    const fetchPayoutRoutes = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Use RPC facade instead of direct supabase client
        const result = await getPayoutRoutes();
        
        if (result.error) {
          console.error('[BankAccountSelector] Payout routes query error:', result.error);
          throw new Error(result.error instanceof Error ? result.error.message : 'Failed to load payout routes');
        }
        
        const mappedRoutes: PayoutRoute[] = (result.data || []).map(route => ({
          id: route.id,  // This is the correct payout_route_id for Edge Function!
          external_account_id: route.external_account_id || route.id,
          payment_rail: (route.rail?.toLowerCase() as PaymentRail) || detectPaymentRail(null, route.currency),
          currency: (route.currency as FiatCurrency) || 'USD',
          bank_account: {
            id: route.id,
            external_account_id: route.external_account_id || route.id,
            bank_name: route.bank_name,
            account_number_masked: '****', // Masked by default
            routing_number_masked: '****',
            currency: route.currency,
            account_type: route.rail?.toLowerCase() || 'ach',
            status: route.status || 'active',
            country: null, // Not available in payout_routes
          },
        }));
        
        setPayoutRoutes(mappedRoutes);
        
        // Auto-select first route if none selected
        if (mappedRoutes.length > 0 && !selectedPayoutRouteId) {
          const firstRoute = mappedRoutes[0];
          onPayoutRouteSelect(firstRoute.id, firstRoute.payment_rail, firstRoute.currency);
        }
      } catch (err: unknown) {
        console.error('[BankAccountSelector] Error:', err);
        setError(err.message || 'Failed to load bank accounts');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPayoutRoutes();
  }, [selectedPayoutRouteId, onPayoutRouteSelect]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <SFIcon icon={sfIcons.loader} size="lg" className="animate-spin text-primary" />
        <p className={cn("text-sm", semanticTokens.fg.muted)}>
          Loading your bank accounts...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn(
        "p-6 rounded-lg border text-center space-y-4",
        semanticTokens.danger.bg,
        semanticTokens.danger.border
      )}>
        <p className={cn("text-sm", semanticTokens.danger.fg)}>{error}</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <Heading level={2} className={cn("text-xl sm:text-heading-lg", semanticTokens.fg.default)}>
          Link your bank account
        </Heading>
        <p className={cn("text-sm sm:text-base", semanticTokens.fg.muted)}>
          Select a bank account to receive your fiat payments
        </p>
      </div>

      {/* Bank Accounts List */}
      {payoutRoutes.length > 0 ? (
        <div className="space-y-3">
          {payoutRoutes.map((route) => {
            const isSelected = selectedPayoutRouteId === route.id;
            const railInfo = PAYMENT_RAIL_INFO[route.payment_rail];
            const account = route.bank_account;
            
            return (
              <button
                key={route.id}
                type="button"
                onClick={() => onPayoutRouteSelect(route.id, route.payment_rail, route.currency)}
                className={cn(
                  "w-full p-4 rounded-xl border-2 flex items-center gap-4 transition-all duration-240 text-left",
                  "hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                  isSelected 
                    ? "border-primary bg-primary/5" 
                    : cn(semanticTokens.border.default, semanticTokens.bg.surface, "hover:border-primary/40")
                )}
                aria-pressed={isSelected}
              >
                <div className={cn(
                  "p-3 rounded-lg shrink-0",
                  isSelected ? "bg-primary/10 text-primary" : cn(semanticTokens.bg.muted, semanticTokens.fg.muted)
                )}>
                  <SFIcon icon={sfIcons.landmark} size="md" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={cn("font-medium truncate", semanticTokens.fg.default)}>
                      {account?.bank_name || 'Bank Account'}
                    </span>
                    <Badge variant="secondary" className="text-xs shrink-0">
                      {route.currency}
                    </Badge>
                  </div>
                  <p className={cn("text-sm truncate", semanticTokens.fg.muted)}>
                    ••••{account?.account_number_masked || '****'}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">
                      {railInfo?.name || route.payment_rail.toUpperCase()}
                    </Badge>
                    <span className={cn("text-xs", semanticTokens.fg.subtle)}>
                      {railInfo?.speed}
                    </span>
                  </div>
                </div>

                <div className={cn(
                  "w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0",
                  isSelected 
                    ? "border-primary bg-primary" 
                    : semanticTokens.border.strong
                )}>
                  {isSelected && <SFIcon icon={sfIcons.check} size="sm" className="text-primary-foreground" />}
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className={cn(
          "p-6 rounded-xl border-2 border-dashed text-center space-y-4",
          semanticTokens.border.default
        )}>
          <div className={cn("p-4 rounded-full w-fit mx-auto", semanticTokens.bg.muted)}>
            <Landmark className={cn("h-8 w-8", semanticTokens.fg.disabled)} />
          </div>
          <div className="space-y-2">
            <h3 className={cn("font-medium", semanticTokens.fg.default)}>
              No bank accounts linked
            </h3>
            <p className={cn("text-sm max-w-sm mx-auto", semanticTokens.fg.muted)}>
              Link a bank account to automatically receive fiat when crypto is deposited to your address.
            </p>
          </div>
          <Button onClick={onLinkNewAccount} className="gap-2">
            <SFIcon icon={sfIcons.add} size="sm" />
            Link Bank Account
          </Button>
        </div>
      )}

      {payoutRoutes.length > 0 && (
        <button
          type="button"
          onClick={onLinkNewAccount}
          className={cn(
            "w-full p-4 rounded-xl border-2 border-dashed flex items-center justify-center gap-2 transition-all duration-240",
            "hover:border-primary/40 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
            semanticTokens.border.default
          )}
        >
          <SFIcon icon={sfIcons.add} size="md" className={semanticTokens.fg.muted} />
          <span className={cn("text-sm font-medium", semanticTokens.fg.muted)}>
            Add another bank account
          </span>
        </button>
      )}

      <div className={cn(
        "p-4 rounded-lg border",
        semanticTokens.info.bg,
        semanticTokens.info.border
      )}>
        <p className={cn("text-sm flex items-start gap-2", semanticTokens.info.fg)}>
          <SFIcon icon={sfIcons.info} size="sm" className="shrink-0 mt-0.5" />
          <span>
            <strong>How it works:</strong> When crypto is sent to your address, we automatically 
            convert it at market rates and send fiat to your selected bank account.
          </span>
        </p>
      </div>
    </div>
  );
};

export default BankAccountSelector;

